import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, useHistory, useLocation } from 'react-router-dom';
import Footer from './components/layouts/Footer/Footer';
import Header from './components/layouts/Header/Header';
import Sidebar from './components/layouts/Sidebar/Sidebar';
import { auth } from './firebase-init';
import BookDetails from './views/app/BookDetails/BookDetails';
import Books from './views/app/Books/Books';
import Contact from './views/app/Contact/Contact';
import Home from './views/app/Home/Home';
import Profile from './views/app/Profile/Profile';
import { useTranslation } from 'react-i18next';
import PersonalInformation from './views/auth/personal-information/personal-information';
import Loading from './components/UI/loading';
import AxiosInstance from './helpers/AxiosInstance';
import NotFound from './views/app/NotFound/NotFound';

import { default as HeaderAuth } from './views/auth/Header/Header';
import ViewBook from './views/app/ViewBook/ViewBook';
import ResetPassword from './views/auth/reset-password/reset-password';
import TermsAndConditions from './views/app/terms-and-conditions';
import BookPurchase from './views/visitor/BookPurchase/BookPurchase';
import PaymentSuccess from './views/visitor/PaymentSuccess/PaymentSuccess';
import TestPage from './views/visitor/TestPage/TestPage';
import Theses from './views/visitor/Theses/Theses';
import ConferenceProceedings from './views/visitor/Publications/ConferenceProceedings';
import PublicationBooks from './views/visitor/Publications/PublicationBooks';
import Reports from './views/visitor/Publications/Reports';
import CouncilBooks from './views/visitor/Publications/CouncilBooks';
import CNERH from './views/visitor/Publications/CNERH';
import ElMadjlisHCI from './views/visitor/Publications/ElMadjlisHCI';
import SubscriptionBanner from './components/SubscriptionBanner/SubscriptionBanner';
import AppDownload from './views/app/AppDownload/AppDownload';

const AppRoutes = (props) => {
  let history = useHistory();
  let location = useLocation();
  const [showPersonalInformation, setShowPersonalInformation] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    setShowResetPassword(location.pathname.includes('reset-password'));
  }, []);

  const { auth_user } = props;
  const [openSidebar, setOpenSidebar] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [apiUser, setApiUser] = useState(null);

  useEffect(() => {
    if (auth_user) {
      getToken().then(() => {
        getUserPersonalInformation(auth_user.uid);
      });
    }
  }, [auth_user]);

  const getToken = async () => {
    if (auth_user) {
      const token = await auth_user.getIdTokenResult();
      setUser(token.claims);
      console.log('token.claims : ', token.claims);
    }
  };

  const getUserPersonalInformation = async (uid) => {
    //http request to the api and see if personal information has been set
    try {
      const response = await AxiosInstance.get(`/auth/get-authenticated-user/${uid}`);
      console.log('API response for user:', response?.data);
      
      if (!response.data) {
        console.log('No personal information found, showing form');
        setShowPersonalInformation(true);
        history.push('/personal-information');
      } else {
        console.log('Personal information found:', response.data.firstName);
        auth.currentUser.updateProfile({ displayName: response.data.firstName + ' ' + response.data.lastName });
        setFirstName(response.data.firstName);
        setShowPersonalInformation(false);
        
        // Update the user object with the complete data including student info
        setUser(prevUser => {
          const updatedUser = {
            ...prevUser,
            ...response.data,
            student: response.data.student
          };
          setApiUser(updatedUser); // Store the API user data
          return updatedUser;
        });
      }
    } catch (error) {
      console.error('Error fetching user personal information:', error);
      // If user doesn't exist in API, show personal information form
      if (error?.response?.status === 404 || error?.response?.status === 401) {
        console.log('User not found in API, showing personal information form');
        setShowPersonalInformation(true);
        history.push('/personal-information');
      }
      return;
    }
  };

  return (
    <div
      style={
        location.pathname.includes('/view-book')
          ? { height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }
          : { display: 'flex', flexDirection: 'column', minHeight: '100vh' }
      }
    >
      {showResetPassword === true ? (
        <div className="container-fluid">
          <div className="row ">
            <HeaderAuth />
            <Route path="/reset-password" component={ResetPassword} />
          </div>
        </div>
      ) : showPersonalInformation === true ? (
        <Route path="/personal-information">
          <HeaderAuth />
          <PersonalInformation />
        </Route>
      ) : (
        <>
          {/* Header and Sidebar for all users - RENDER FIRST */}
          {showPersonalInformation !== true && !location.pathname.includes('/view-book') && (
            <>
              <Header openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} user={user} firstName={firstName} />
              <Sidebar openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} user={user} />
              {user && <SubscriptionBanner user={apiUser} />}
            </>
          )}

          <div style={{ flex: 1 }}>
          <Switch>
            {/* Visitor routes - accessible to everyone */}
            <Route exact path="/visitor/test" component={TestPage} />
            <Route exact path="/visitor/books" component={BookPurchase} />
            <Route exact path="/visitor/payment/success" component={PaymentSuccess} />
            
            {/* Public routes accessible to everyone */}
            <Route exact path="/" component={Home} />
            <Route path="/books" component={Books} />
            <Route exact path="/theses" component={Theses} />
            <Route exact path="/publications/conference-proceedings" component={ConferenceProceedings} />
            <Route exact path="/publications/books" component={PublicationBooks} />
            <Route exact path="/publications/reports" component={Reports} />
            <Route exact path="/publications/council-books" component={CouncilBooks} />
            <Route exact path="/publications/cnerh" component={CNERH} />
            <Route exact path="/publications/el-madjlis-hci" component={ElMadjlisHCI} />
            <Route exact path="/book-details/:id" render={(props) => <BookDetails {...props} user={apiUser} />} />
            <Route exact path="/contact" component={Contact} />
            <Route exact path="/app" component={AppDownload} />
            <Route exact path="/view-book/:id" render={(props) => <ViewBook {...props} user={apiUser} />} />
            <Route exact path="/terms-of-service" component={TermsAndConditions} />
            
            {/* Authenticated user routes */}
            {showPersonalInformation === false && (
              <>
                <Route exact path="/profile" render={(props) => <Profile {...props} user={apiUser} />} />
                <Route exact path="/home">
                  <Redirect to="/" />
                </Route>
              </>
            )}
            
            <Route path="/" component={NotFound} />
          </Switch>
          </div>

          {!location.pathname.includes('/view-book') && (
            <>
              <Footer />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AppRoutes;